import React, { ReactNode } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";

export interface AppDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Confirm body rendered as DialogContentText when string/node (no form children). */
  message?: ReactNode;
  /** Form / custom body content. */
  children?: ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  fullWidth?: boolean;
  dialogId?: string;
  titleId?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  hideCancel?: boolean;
  hideActions?: boolean;
  confirmColor?: "primary" | "error";
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  confirmLoading?: boolean;
  cancelButtonId?: string;
  confirmButtonId?: string;
  confirmStartIcon?: ReactNode;
}

/**
 * Shared dialog matching Settings confirmation UI/UX:
 * outlined Cancel + contained Confirm with Theme btn_secondary / btn_primary styles.
 */
const AppDialog: React.FC<AppDialogProps> = ({
  open,
  onClose,
  title,
  message,
  children,
  maxWidth,
  fullWidth,
  dialogId,
  titleId = "app-dialog-title",
  cancelLabel = "Cancel",
  confirmLabel = "OK",
  onCancel,
  onConfirm,
  hideCancel = false,
  hideActions = false,
  confirmColor,
  confirmDisabled = false,
  cancelDisabled = false,
  confirmLoading = false,
  cancelButtonId = "btn_cancel",
  confirmButtonId,
  confirmStartIcon,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);

  const isForm = Boolean(children);
  const resolvedMaxWidth = maxWidth !== undefined ? maxWidth : isForm ? "md" : undefined;
  const resolvedFullWidth = fullWidth !== undefined ? fullWidth : isForm;
  const resolvedConfirmColor = confirmColor ?? "primary";
  const handleCancel = onCancel ?? onClose;

  return (
    <Dialog
      id={dialogId}
      open={open}
      onClose={confirmLoading ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth={resolvedMaxWidth}
      fullWidth={resolvedFullWidth}
      disableEscapeKeyDown={confirmLoading}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        {message != null && message !== "" && (
          <DialogContentText component="div" sx={children ? { mb: 2 } : undefined}>
            {message}
          </DialogContentText>
        )}
        {children}
      </DialogContent>
      {!hideActions && (
        <DialogActions>
          {!hideCancel && (
            <Button
              id={cancelButtonId}
              onClick={handleCancel}
              variant="outlined"
              sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
              disabled={confirmLoading || cancelDisabled}
            >
              {cancelLabel}
            </Button>
          )}
          {onConfirm && (
            <Button
              id={confirmButtonId}
              onClick={onConfirm}
              color={resolvedConfirmColor}
              sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
              variant="contained"
              disabled={confirmLoading || confirmDisabled}
              startIcon={confirmStartIcon}
            >
              {confirmLabel}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AppDialog;
